import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Search, Upload, FileText, Image as ImageIcon, Check, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const ax = axios.create({ baseURL: API, withCredentials: true });
const mediaUrl = (m) => (m.url?.startsWith("http") ? m.url : `${process.env.REACT_APP_BACKEND_URL}${m.url}`);

/**
 * Reusable media picker modal.
 * Props:
 *  - kind: "image" | "pdf" | "all" (default "all")
 *  - onClose()
 *  - onSelect({id, url, alt, filename})
 */
export default function MediaPicker({ kind = "all", onClose, onSelect }) {
  const [tab, setTab] = useState("library");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState(kind === "all" ? "all" : kind);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  // Upload form state
  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState("");
  const [tag, setTag] = useState("");
  const [makeTransparent, setMakeTransparent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const PAGE_SIZE = 24;

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get("/media");
      setItems(data || []);
    } catch (e) {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  // close on Esc
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  const filtered = useMemo(() => {
    const f = filter === "all" ? null : filter;
    return (items || []).filter((m) => {
      if (f && m.kind !== f) return false;
      if (!search) return true;
      const t = `${m.filename || ""} ${m.alt || ""} ${m.tag || ""}`.toLowerCase();
      return t.includes(search.toLowerCase());
    });
  }, [items, filter, search]);

  const pageItems = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const submitUpload = async () => {
    if (!file) return toast.error("Choose a file first.");
    if (!alt) return toast.error("Alt text is required for accessibility.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", alt);
      fd.append("tag", tag);
      if (makeTransparent) fd.append("make_transparent_flag", "1");
      const { data } = await ax.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setFile(null); setAlt(""); setTag(""); setMakeTransparent(false);
      await refresh();
      setTab("library");
      // Auto-select the freshly uploaded asset for convenience.
      if (data) onSelect?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPick = (m) => {
    onSelect?.({ id: m.id, url: m.url, alt: m.alt || "", filename: m.filename, kind: m.kind });
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4"
      role="dialog" aria-modal="true"
      data-testid="media-picker-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-5xl max-h-[90vh] rounded-3xl bg-white shadow-card border border-navy-100 flex flex-col overflow-hidden">
        <header className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setTab("library")}
              data-testid="media-picker-tab-library"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "library" ? "bg-navy text-white" : "text-navy-700 hover:bg-navy-50"}`}
            >Library</button>
            <button
              onClick={() => setTab("upload")}
              data-testid="media-picker-tab-upload"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === "upload" ? "bg-navy text-white" : "text-navy-700 hover:bg-navy-50"}`}
            >Upload</button>
          </div>
          <button onClick={onClose} data-testid="media-picker-close" className="p-2 rounded-full hover:bg-navy-50 text-navy-700">
            <X className="h-5 w-5" />
          </button>
        </header>

        {tab === "library" && (
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy-400" />
                <input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  placeholder="Search by filename, alt or tag"
                  data-testid="media-picker-search"
                  className="w-full rounded-full border border-navy-100 bg-white pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-teal"
                />
              </div>
              <div className="flex gap-1.5">
                {[
                  { v: "all", label: "All" },
                  { v: "image", label: "Images" },
                  { v: "pdf", label: "PDFs" },
                ].map((f) => (
                  <button
                    key={f.v}
                    onClick={() => { setFilter(f.v); setPage(0); }}
                    data-testid={`media-picker-filter-${f.v}`}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${filter === f.v ? "bg-teal text-white" : "bg-soft-gray text-navy-700 hover:bg-navy-50"}`}
                  >{f.label}</button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
            ) : pageItems.length === 0 ? (
              <div className="py-16 text-center text-navy-500">
                <ImageIcon className="h-10 w-10 mx-auto text-navy-200 mb-3" />
                <p className="text-sm">No media found. Try adjusting your filters or upload a new file.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="media-picker-grid">
                {pageItems.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => onPick(m)}
                    data-testid={`media-picker-item-${m.id}`}
                    className="group relative aspect-square rounded-xl overflow-hidden border border-navy-100 bg-soft-gray hover:border-teal hover:shadow-soft transition-all text-left"
                  >
                    {m.kind === "image" ? (
                      <img src={mediaUrl(m)} alt={m.alt || m.filename} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <div className="h-full w-full flex flex-col items-center justify-center p-3">
                        <FileText className="h-7 w-7 text-navy-500" />
                        <p className="mt-2 text-[10px] text-navy-700 truncate w-full text-center">{m.filename}</p>
                      </div>
                    )}
                    <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/40 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-teal text-white text-xs font-semibold">
                        <Check className="h-3.5 w-3.5" /> Insert
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-6 flex justify-center gap-1.5">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`h-8 w-8 rounded-full text-xs font-semibold ${i === page ? "bg-navy text-white" : "bg-soft-gray text-navy-700 hover:bg-navy-50"}`}
                  >{i + 1}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "upload" && (
          <div className="flex-1 overflow-y-auto p-6">
            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => {
                e.preventDefault(); setDrag(false);
                const f = e.dataTransfer.files?.[0];
                if (f) setFile(f);
              }}
              className={`rounded-3xl border-2 border-dashed p-10 text-center transition-colors ${drag ? "border-teal bg-teal/5" : "border-navy-200 bg-soft-gray"}`}
              data-testid="media-picker-dropzone"
            >
              <Upload className="h-10 w-10 mx-auto text-navy-400" />
              <p className="mt-3 text-sm text-navy-700 font-semibold">
                {file ? file.name : "Drag & drop a file here"}
              </p>
              <p className="text-xs text-navy-500 mt-1">PNG, JPG, WEBP, SVG up to 20 MB · PDF up to 100 MB</p>
              <label className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-navy text-white text-xs font-semibold cursor-pointer hover:bg-teal transition-colors">
                Choose file
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="media-picker-file-input"
                />
              </label>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Alt text (required)</span>
                <input
                  value={alt}
                  onChange={(e) => setAlt(e.target.value)}
                  placeholder="Describe the image for screen readers"
                  className="w-full rounded-xl border border-navy-100 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-teal"
                  data-testid="media-picker-alt"
                />
              </label>
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.18em] font-semibold text-navy-500 mb-2">Tag</span>
                <input
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  placeholder="e.g. hero, logo, gallery"
                  className="w-full rounded-xl border border-navy-100 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-teal"
                  data-testid="media-picker-tag"
                />
              </label>
            </div>

            <label className="mt-4 inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={makeTransparent}
                onChange={(e) => setMakeTransparent(e.target.checked)}
                className="h-4 w-4 accent-teal"
                data-testid="media-picker-transparent"
              />
              <span className="text-sm text-navy-700">Strip near-white background (for logos)</span>
            </label>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-navy-700 hover:bg-navy-50"
              >Cancel</button>
              <button
                onClick={submitUpload}
                disabled={uploading || !file || !alt}
                data-testid="media-picker-submit-upload"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-teal text-white text-sm font-semibold hover:bg-teal-light hover:text-navy transition-colors disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload & insert
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
